import {passingFailure, withoutCodes} from './queue';
import {queueText} from './queueText';
import {studioText} from './studioText';
import {
  ApiError,
  GENERIC_ERROR,
  isCreditCheckPending,
  isInsufficientCredits,
  looksTechnical,
  safeMessage,
} from '../../data/models/Studio/StudioError';

// The request errors and their checks belong to the data layer that sends Studio's requests;
// the Studio's own code keeps importing them from here.
export {
  ApiError,
  CREDIT_CHECK_PENDING,
  CREDIT_CHECK_PENDING_CODE,
  GENERIC_ERROR,
  INSUFFICIENT_CREDITS,
  isCreditCheckPending,
  isInsufficientCredits,
  looksTechnical,
  safeMessage,
} from '../../data/models/Studio/StudioError';

/**
 * The message to show for a failed action. Studio requests and Studio's own checks
 * throw messages for authors; anything else (a failed fetch, a bug) is not one.
 */
export function errorMessage(error: unknown, enforceAiCredits = true): string {
  if (
    !enforceAiCredits &&
    (isCreditCheckPending(error) || isInsufficientCredits(error))
  )
    return GENERIC_ERROR;
  return error instanceof ApiError ||
    (error instanceof Error && error.constructor === Error)
    ? safeMessage(error.message)
    : GENERIC_ERROR;
}

// Older servers stopped a render whose narration ran past its length band: 'The narration of
// "Fire drills" runs 64s, longer than the 60s the selected length allows. Cut about 10 words
// from its storyboard and generate again.'
const RAN_PAST_LENGTH =
  /^The narration of "(.+)" runs (\d+)s, longer than the (\d+)s the selected length allows\.(?: Cut about (\d+) words? from)?/;

/**
 * Why a job failed, for its failed screen: always something to show. The length a microreel
 * aims for is an estimate, so a render an older server stopped for running past it says so
 * calmly. Only an older render worker stops there, and it stops there again, so the words it
 * asked to cut stay as what to do if it does.
 */
export function jobError(
  text: string | null | undefined,
  code?: string | null,
): string {
  // A failure that passes (Studio's AI service at capacity, Studio too busy) says so in Studio's
  // own words, with Continue to pick up where it stopped.
  const passing = passingFailure(text, code);
  if (passing === 'provider-capacity') return queueText.providerCapacityFailed;
  if (passing === 'transient') return queueText.transientFailed;
  const long = RAN_PAST_LENGTH.exec(withoutCodes(text || ''));
  return long
    ? studioText.ranPastEstimate(
        long[1],
        Number(long[2]),
        Number(long[3]),
        long[4] ? Number(long[4]) : undefined,
      )
    : safeMessage(withoutCodes(text || ''));
}

// An older server's note on a microreel that runs past its length band: "r1: measured duration
// 61.2s exceeds the selected 30-60s band (60s maximum). Narration is preserved; shorten the
// storyboard to fit."
const PAST_LENGTH_NOTE = /\bmeasured duration [\d.]+s exceeds the selected\b/i;

/** A note that treats the length a microreel aims for as a limit; the length is an estimate. */
export const isLengthNote = (note: string) => PAST_LENGTH_NOTE.test(note);

// The render's note on subtitle languages it couldn't translate: "Subtitles could not be
// translated into Czech. Render again to try those languages again."
const SUBTITLE_NOTE = /\bsubtitles?\b/i;

/**
 * A note about subtitles. Authors aren't shown which languages get subtitles, so a language
 * that didn't would only confuse them; these notes stay hidden for now.
 */
export const isSubtitleNote = (note: string) => SUBTITLE_NOTE.test(note);

/** Why Generate stays disabled when every render blocker is technical. */
export const RENDER_BLOCKED = studioText.renderBlocked;

/**
 * The render blockers an author can fix. When every blocker is technical,
 * RENDER_BLOCKED still says why the button is disabled.
 */
export function authorReasons(reasons: string[] | null | undefined): string[] {
  const all = reasons || [];
  const kept = all.filter(reason => !looksTechnical(reason));
  return kept.length || !all.length ? kept : [RENDER_BLOCKED];
}

/**
 * Render notes an author can act on. A technical detail in trailing parentheses
 * ("rendered without it (OpenRouter …).") is dropped; a technical note is hidden.
 */
export function authorNotes(notes: string[] | null | undefined): string[] {
  return (notes || [])
    .map(note =>
      note.replace(
        /\s*\(([^()]*(?:\([^()]*\)[^()]*)*)\)(\.?)$/,
        (detail, inner: string, stop: string) =>
          looksTechnical(inner) ? stop : detail,
      ),
    )
    .filter(note => note.trim() && !looksTechnical(note));
}
